import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, openAuthedPdf } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";

// Email a client a statement of every unpaid invoice (PDF attached). client = { name, depot_id }.
export const StatementDialog = ({ client, onClose }) => {
  const [st, setSt] = useState(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!client) return;
    setSt(null);
    api.statementPreview(client.name, client.depot_id).then((s) => { setSt(s); setTo(s.client.email || ""); })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load statement"); onClose(); });
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps
  const q = client ? new URLSearchParams({ client: client.name, ...(client.depot_id ? { depot_id: client.depot_id } : {}) }).toString() : "";
  const send = async () => {
    setBusy(true);
    try { const r = await api.statementSend({ client: client.name, depot_id: client.depot_id || null, to }); toast.success(`Statement sent to ${r.sent_to}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send statement"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Statement for {client?.name}</DialogTitle>
          <DialogDescription>Every unpaid invoice and the total owing, with a PDF attached.</DialogDescription>
        </DialogHeader>
        {!st ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="rounded-xl border border-border divide-y divide-border" data-testid="statement-invoices">
              {st.invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`statement-row-${i.id}`}>
                  <div><p className="font-semibold">{i.number} <span className="font-normal text-muted-foreground">· {i.job_name}</span></p>
                    <p className={`text-xs ${i.overdue ? "text-red-600" : "text-muted-foreground"}`}>{i.overdue ? "Overdue, was due" : "Due"} {fmtDate(i.due_date)}</p></div>
                  <span className="font-semibold tabular-nums">{fmtMoney(i.total_c)}</span>
                </div>
              ))}
              <div className="flex justify-between px-3 py-2.5 font-bold"><span>Total owing</span><span className="tabular-nums" data-testid="statement-total">{fmtMoney(st.owing_c)}</span></div>
            </div>
            {st.overdue_c > 0 && <p className="text-sm text-red-600" data-testid="statement-overdue">{fmtMoney(st.overdue_c)} of this is overdue.</p>}
            <div className="space-y-1.5"><Label className="text-xs">Send to</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="statement-to" /></div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={!st} onClick={() => openAuthedPdf(`/statements/pdf?${q}`)} className="rounded-full" data-testid="statement-pdf-btn"><FileText className="w-4 h-4 mr-2" /> Preview PDF</Button>
          <Button disabled={!st || busy || !to} onClick={send} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="statement-send-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send statement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Confirm, then email one polite reminder per client listing all their overdue invoices.
export const ChaseDialog = ({ open, onClose, onSent }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setP(null); api.chasePreview().then(setP).catch(() => { toast.error("Could not load overdue invoices"); onClose(); }); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { const r = await api.chaseSend(); toast.success(`Reminders are on their way to ${r.clients} client${r.clients === 1 ? "" : "s"}`); onSent?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminders"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="chase-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Chase all overdue</DialogTitle>
          <DialogDescription>Each client gets one polite email listing all their overdue invoices, with pay links.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="space-y-3 text-sm">
            {p.clients.length === 0 ? <p className="text-muted-foreground" data-testid="chase-nothing">Nothing to chase right now.</p> : (
              <div className="rounded-xl border border-border divide-y divide-border" data-testid="chase-clients">
                {p.clients.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`chase-client-${i}`}>
                    <div className="min-w-0"><p className="font-semibold truncate">{c.name}</p><p className="text-xs text-muted-foreground truncate">{c.email} · {c.count} invoice{c.count === 1 ? "" : "s"}</p></div>
                    <span className="font-semibold text-red-600 tabular-nums">{fmtMoney(c.overdue_c)}</span>
                  </div>
                ))}
              </div>
            )}
            {p.skipped_recent > 0 && <p className="text-muted-foreground" data-testid="chase-skipped">Skipping {p.skipped_recent} invoice{p.skipped_recent === 1 ? "" : "s"} already reminded in the last 7 days.</p>}
            {p.no_email.length > 0 && <p className="text-amber-700 inline-flex gap-1.5" data-testid="chase-no-email"><AlertTriangle className="w-4 h-4 shrink-0" /> Can't email {p.no_email.map((c) => c.name).join(", ")}: add a valid email on the client card{p.no_email.some((c) => c.email) ? " (the demo company can only email the Cross Jobs inbox)" : ""}.</p>}
          </div>
        )}
        <DialogFooter>
          <Button disabled={!p?.clients.length || busy} onClick={send} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="chase-send-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {p?.clients.length ? `Email ${p.clients.length} client${p.clients.length === 1 ? "" : "s"} (${fmtMoney(p.total_c)})` : "Send reminders"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
